import { useState, useEffect } from "react";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ETAPA_GRADIENTE } from "@/data/artigosData";
import { getJSON } from "@/lib/api";

const FILTROS = ["Todos", "Planejamento", "Gestação", "Parto", "Pós-parto"];

const Conteudos = () => {
  const [filtro, setFiltro] = useState("Todos");
  const [artigos, setArtigos] = useState([]);

  useEffect(() => {
    getJSON("/conteudos").then(setArtigos).catch(() => setArtigos([]));
  }, []);

  const base = filtro === "Todos" ? artigos : artigos.filter((a) => a.etapa === filtro);
  const lista = base.slice(0, 6);

  return (
    <section id="conteudos" data-testid="conteudos-section" className="bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="max-w-2xl">
            <span className="text-sm font-semibold tracking-widest text-maternnia-pink uppercase">Conteúdos em destaque</span>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-maternnia-charcoal mt-4 font-medium">
              Informação confiável para decisões mais conscientes.
            </h2>
          </div>
          <Link to="/conteudos" data-testid="conteudos-ver-todos" className="group inline-flex items-center gap-2 text-maternnia-eucalipto hover:text-maternnia-floresta font-semibold transition-colors whitespace-nowrap">
            Ver todos os conteúdos
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2.5 mb-12">
          {FILTROS.map((f) => (
            <button
              key={f}
              data-testid={`filtro-${f}`}
              onClick={() => setFiltro(f)}
              className={`text-sm font-medium rounded-full px-5 py-2.5 transition-all duration-300 ${
                filtro === f
                  ? "bg-maternnia-charcoal text-white"
                  : "bg-maternnia-porcelana text-maternnia-taupe hover:bg-maternnia-nevoa"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-8">
          {lista.map((a, i) => (
            <motion.div
              key={a.id}
              layout
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to={`/conteudos/${a.slug}`}
                data-testid={`article-${a.id}`}
                className="group block bg-white rounded-3xl overflow-hidden border border-maternnia-nevoa/60 shadow-sm hover:shadow-[0_16px_40px_rgba(43,37,38,0.08)] hover:-translate-y-1 transition-all duration-300 h-full"
              >
                <div className={`overflow-hidden relative ${a.imgPortrait ? "aspect-[4/5]" : "aspect-[16/10]"}`}>
                  <img src={a.imagem} alt={a.imagemAlt} loading="lazy" className={`w-full h-full object-cover ${a.imgPos || ""} group-hover:scale-105 transition-transform duration-500`} />
                  <span className={`absolute top-4 left-4 text-xs font-semibold tracking-widest uppercase bg-gradient-to-br ${ETAPA_GRADIENTE[a.etapa]} bg-white/90 px-3 py-1.5 rounded-full`}>{a.etapa}</span>
                </div>
                <div className="p-7">
                  <h3 className="font-serif text-2xl text-maternnia-charcoal leading-snug font-medium group-hover:text-maternnia-eucalipto transition-colors">
                    {a.titulo}
                  </h3>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-xs text-maternnia-taupe font-light">Leitura de {a.tempoLeitura}</span>
                    <ArrowUpRight size={18} className="text-maternnia-eucalipto transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Conteudos;
